// FeedbackStatsRow.tsx — four stat tiles (Total / Under review / Planned /
// Completed) driven by GET /feedback/stats/ (task spec §7 — the endpoint
// stays backward compatible: it still returns every
// bucket, including inProgress/testing/declined, which this row simply no
// longer surfaces as its own tile).
import styled from 'styled-components';
import { softCardInteraction } from '@/theme/interactions';
import { Icon } from '@/icons';
import type { IconName } from '@/icons';
import type { FeedbackStats } from '@/core/models/feedback';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin: 24px 0;

  @media (max-width: 1300px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Tile = styled.div<{ $index?: number }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 18px 14px;
  position: relative;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.color.surface};
  background-image: linear-gradient(120deg, ${({ theme }) => theme.color.brandFrom}10, transparent);
  ${softCardInteraction}
  > div > span:first-child {
    color: ${({ theme, $index }) =>
      $index === 3
        ? theme.color.success
        : $index === 2
          ? theme.color.accentCyan
          : theme.color.brandFrom};
  }
`;

const TileHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  > span:first-child {
    padding: 7px;
    background: ${({ theme }) => theme.color.brandFrom}0d;
    border-radius: 8px;
  }
  > div {
    min-width: 0;
  }
  color: ${({ theme }) => theme.color.textMuted};
  @media (max-width: 640px) {
    display: block;
    position: relative;
    > span:first-child {
      position: absolute;
      right: 0;
      top: 28px;
      padding: 3px;
      svg {
        width: 18px;
        height: 18px;
      }
    }
  }
`;

const TileLabel = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  line-height: 1.4;
`;

const TileValue = styled.span`
  display: block;
  margin: 3px 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: 28px;
  line-height: 1.3;
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
  font-variant-numeric: tabular-nums;
`;

const TileSub = styled.span`
  display: block;
  margin-top: 6px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.success};
`;

const SkeletonTile = styled(Tile)`
  opacity: 0.5;
  min-height: 66px;
`;

interface TileDef {
  key: keyof FeedbackStats;
  label: string;
  icon: IconName;
  description: string;
}

const TILES: TileDef[] = [
  {
    key: 'total',
    label: 'Total feedback',
    icon: 'file-text',
    description: 'Ideas from the community',
  },
  { key: 'underReview', label: 'Under review', icon: 'clock', description: 'Being evaluated' },
  { key: 'planned', label: 'Planned', icon: 'package', description: 'On the roadmap' },
  { key: 'completed', label: 'Completed', icon: 'check', description: 'Completed feedback' },
];

export interface FeedbackStatsRowProps {
  stats: FeedbackStats | null;
  loading: boolean;
}

export function FeedbackStatsRow({ stats, loading }: FeedbackStatsRowProps) {
  if (loading || !stats) {
    return (
      <Grid aria-busy="true" data-testid="feedback-stats-loading">
        {TILES.map((t) => (
          <SkeletonTile key={t.key} />
        ))}
      </Grid>
    );
  }

  return (
    <Grid role="group" aria-label="Feedback board stats">
      {TILES.map((t, index) => (
        <Tile $index={index} key={t.key} data-testid={`feedback-stat-${t.key}`}>
          <TileHead>
            <Icon name={t.icon} size={25} />
            <div>
              <TileLabel>{t.label}</TileLabel>
              <TileValue>{String(stats[t.key])}</TileValue>
              <TileLabel>{t.description}</TileLabel>
              {t.key === 'total' && stats.newThisWeek > 0 && (
                <TileSub>+{stats.newThisWeek} this week</TileSub>
              )}
            </div>
          </TileHead>
        </Tile>
      ))}
    </Grid>
  );
}
